import { sectorBadgeType } from 'constants/companySectors';
import { eventCategoryText } from 'constants/eventCategories';
import { addDays, clockLabel, fromIsoDay, isoDay, shortDayLabel } from 'helper/timeLabels';
import { NO_CRM_FILTERS } from 'server/crm/crmCompanies';

// The filters of the list: the real ones (sent to admin/getCompaniesList),
// then those of the CRM (action, commercial).
export const NO_FILTERS = {
  departments: [],
  category: '',
  suppliers: [],
  missionCount: '',
  missionCountMin: '',
  incompleteRemuneration: false,
  ...NO_CRM_FILTERS,
};

export const activeFilterCount = (filters) =>
  [
    filters.departments.length > 0,
    Boolean(filters.category),
    filters.suppliers.length > 0,
    filters.missionCount === 'never' || (filters.missionCount === 'at_least' && Boolean(filters.missionCountMin)),
    filters.incompleteRemuneration,
    filters.action !== 'all',
    filters.commercial !== 'all',
  ].filter(Boolean).length;

const SMALL_WORDS = ['de', 'du', 'des', 'la', 'le', 'les', 'et', 'sur', 'en'];
// « MARTINIQUE » and « HAUTS-DE-SEINE » as « Martinique » and « Hauts-de-Seine »;
// a name already written in small letters stays as it is.
export const departmentLabel = (department) => {
  if (!department) return '';
  if (department !== department.toUpperCase()) return department;
  return department
    .toLowerCase()
    .replace(/[^\s-]+/g, (word, offset) =>
      offset > 0 && SMALL_WORDS.includes(word) ? word : word.charAt(0).toUpperCase() + word.slice(1),
    );
};

const DAYS_SHORT = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];

// « 1er oct. », with the year when it is not this one: « 21 nov. 2025 ».
export const dateLabel = (day, now) => {
  const label = shortDayLabel(day).replace(/^1 /, '1er ');
  return day.slice(0, 4) === String(now.getFullYear()) ? label : `${label} ${day.slice(0, 4)}`;
};
// « oct. 2024 »
const monthLabel = (day) => fromIsoDay(day).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });

// When something happens: its time today, its day within the week, its date
// further away.
const whenLabel = (date, now, withTime) => {
  const day = isoDay(date);
  if (day === isoDay(now)) return clockLabel(date);
  const close = day > isoDay(addDays(now, -7)) && day < isoDay(addDays(now, 7));
  const label = close ? DAYS_SHORT[date.getDay()] : dateLabel(day, now);
  return withTime ? `${label} ${clockLabel(date)}` : label;
};

// « Rappel 14:30 », « Visite de suivi jeu. » or the title of the task.
export const actionLabel = (action, { now, text, language }) => {
  if (!action) return '';
  if (action.kind === 'callback') return text('actionCallback', { when: whenLabel(new Date(action.at), now, true) });
  if (action.kind === 'appointment') {
    return `${eventCategoryText(language, action.category, 'title')} ${whenLabel(new Date(action.at), now, false)}`;
  }
  return action.title;
};

/**
 * What a row shows of a company, beyond its own fields: its department, its
 * Pennylane category (`categories`: { id, label } of the api) and its badge,
 * its groups, its next action and the date of its tab.
 */
export const describeCompany = (company, { tab, now, text, language, categories = [] }) => {
  const category = categories.find((item) => String(item.id) === String(company.pennylane_category_id));
  const created = company.createdAt ? isoDay(new Date(company.createdAt)) : null;
  let since = '';
  if (tab === 'prospects') since = created ? text('createdOn', { day: dateLabel(created, now) }) : '';
  else if (company.clientSince) {
    since = tab === 'new' ? dateLabel(company.clientSince, now) : monthLabel(company.clientSince);
  }
  let relaunch = text('noRelaunch');
  if (company.relaunchOn) {
    const day = fromIsoDay(company.relaunchOn);
    const month = day.toLocaleDateString('fr-FR', { month: 'short' });
    relaunch = text('relaunchIn', {
      month: day.getFullYear() === now.getFullYear() ? month : `${month} ${day.getFullYear()}`,
    });
  }
  return {
    ...company,
    name: company.name_commercial || company.reason_social || '',
    department: departmentLabel(company.department),
    category: category ? { label: category.label, badge: sectorBadgeType(category.label) } : null,
    groups: company.group?.name ?? (company.bigAccounts ?? []).join(', '),
    groupId: company.group?.id ?? null,
    action: actionLabel(company.nextAction, { now, text, language }),
    since,
    relaunch,
    lostOn: company.lostAt ? dateLabel(company.lostAt, now) : '',
  };
};
